import type { SyntheticEvent } from 'react';
import React from 'react';

import { useHistory } from 'react-router';

import type { LinkProps as MuiLinkProps, Theme } from '@material-ui/core';
import { Link as MuiLink } from '@material-ui/core';

import type { KlpStyled } from '../Theme';
import { removeKlpStyles } from '../Theme';
import type { WithTranslation } from '../TranslationOrChildren';
import { TranslationOrChildren } from '../TranslationOrChildren';
import { useKlpStyles } from '../Theme/useKlpStyles';
import classNames from 'classnames';

type LinkOwnProps = {
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    href?: string;
    target?: string;
    externalLink?: boolean;
    onClick?: (event: SyntheticEvent) => void;
    disabled?: boolean;
    forwardedRef?: React.Ref<HTMLAnchorElement>;
};

type LinkProps = LinkOwnProps & WithTranslation & MuiLinkProps & KlpStyled;

const styles = (theme: Theme) => ({
    linkContainer: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(0.5),
    },
    link: {
        cursor: 'pointer',
    },
});

export const Link = (props: React.PropsWithChildren<LinkProps>) => {
    const {
        startIcon,
        endIcon,
        href,
        target,
        externalLink,
        onClick,
        children,
        translation,
        translationOptions,
        className = '',
        forwardedRef,
        ...restProps
    } = props;

    const klpStyles = useKlpStyles(props, styles);
    const history = useHistory();

    const handleClick = (event: SyntheticEvent) => {
        event.preventDefault();

        if (onClick) onClick(event);
        if (!href) return;

        if (href.startsWith('mailto:')) {
            window.open(href, target);
        } else if (target) {
            window.open(href, target);
        } else if (!href.startsWith('http')) {
            history.push(href);
        } else {
            window.location.href = href;
        }
    };

    const externalLinkProps = externalLink ? { rel: 'noreferrer' } : {};

    return (
        <span className={klpStyles.linkContainer} onClick={handleClick}>
            {startIcon}
            <MuiLink
                {...externalLinkProps}
                className={classNames(
                    klpStyles.klpStyles,
                    klpStyles.link,
                    className,
                )}
                href={href}
                component="a"
                ref={forwardedRef}
                {...removeKlpStyles(restProps)}
            >
                <TranslationOrChildren
                    translation={translation}
                    translationOptions={translationOptions}
                >
                    {children}
                </TranslationOrChildren>
            </MuiLink>
            {endIcon}
        </span>
    );
};
